import { graphql } from '@/service/graphql'
import { gqlFetch } from '@/service/gql'
import { getAuth } from '@/auth/authRef'
import type { ResultOf } from 'gql.tada'
import { ctx } from '@/util/context'
import { createPersistentItem, logDebug, logWarn } from '@amazon/vinyl-util'
import { createLocalOverlay } from '@/util/localOverlay'

export type LikeState = 'LIKE' | 'DISLIKE'

export const trackLikesContextFrag = graphql(`
    fragment trackLikesContext on Track @_unmask {
        id
        likesContext @include(if: $isAuthenticated) {
            liked
        }
    }
`)

export type TrackLikesContext = ResultOf<typeof trackLikesContextFrag>

const likeTrackQuery = graphql(
    `
        mutation setTrackLikeState(
            $likeState: String
            $trackId: String!
            $isAuthenticated: Boolean!
        ) {
            setTrackLikeState(likeState: $likeState, trackId: $trackId) {
                likeState
                track {
                    ...trackLikesContext
                }
            }
        }
    `,
    [trackLikesContextFrag]
)

export type LikeTrackQueryResult = ResultOf<typeof likeTrackQuery>

export interface TrackLikeOptions {
    readonly trackId: string
    readonly likeState: LikeState
}

const { target } = ctx('trackLikeService')

// Like-state overlay. Same story as the tastes follow overlay: even
// when the mutation succeeds, the gateway's cached track responses can
// keep returning the pre-mutation `liked` state for tens of seconds.
// The overlay holds our chosen value for the catch-up window so reads
// during that period reflect what the user actually did.
const LIKE_OVERLAY_TTL_MS = 60_000
const likeOverlay = createLocalOverlay<string, boolean>({
    persistence: createPersistentItem({
        key: 'overlay:tracks:liked',
        defaultValue: {},
    }),
    validateValue: (v): v is boolean => typeof v === 'boolean',
})

/**
 * Read the optional local like override for `trackId`. Returns true /
 * false when the overlay holds a fresh decision, undefined otherwise.
 * Callers should prefer this over the server payload until it expires.
 */
export function readTrackLikeOverlay(trackId: string): boolean | undefined {
    return likeOverlay.read(trackId)
}

/**
 * Wait for persisted overlay state to hydrate before reading. Mirrors
 * the tastes overlay's `whenHydrated` — without this a cold-start read
 * can race the persistence load.
 */
export function whenTrackLikeOverlayHydrated(): Promise<void> {
    return likeOverlay.whenHydrated()
}

/**
 * Apply the local overlay to a freshly fetched track-like context.
 * Returns the input unchanged if no overlay entry is present.
 */
export function applyTrackLikeOverlay<T extends TrackLikesContext | null | undefined>(track: T): T {
    if (!track?.id) return track
    const override = likeOverlay.read(track.id)
    if (override === undefined) return track
    return {
        ...track,
        likesContext: { liked: override },
    } as T
}

export async function setTrackLikeState(options: TrackLikeOptions): Promise<LikeTrackQueryResult> {
    logDebug(target, options)
    const newLiked = options.likeState === 'LIKE'
    // Lay the change down in the overlay before the network round trip
    // so any read that fires while the mutation is in flight already
    // sees the new state.
    likeOverlay.write(options.trackId, newLiked, { ttlMs: LIKE_OVERLAY_TTL_MS })
    try {
        return await gqlFetch(likeTrackQuery, {
            ...options,
            isAuthenticated: getAuth().isSignedIn(),
        })
    } catch (e) {
        // On failure, drop the optimistic overlay so the UI reverts.
        logWarn(target, 'setTrackLikeState failed, reverting overlay', {
            trackId: options.trackId,
            error: (e as Error).message,
        })
        likeOverlay.clear(options.trackId)
        throw e
    }
}
